import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { enqueueSnackbar } from 'notistack';

export interface QuickAddEntity {
  name: string;
  entity_type: string;
  country: string;
  address: string;
}

type RowStatus = 'draft' | 'saved' | 'error';

interface QuickAddRow {
  id: string;
  name: string;
  entity_type: string;
  country: string;
  address: string;
  status: RowStatus;
  errors: Partial<Record<keyof QuickAddEntity, string>>;
}

interface IQuickAddEntitiesProps {
  onSaveAll: (entities: QuickAddEntity[]) => void;
  onCheckDuplicate?: (entity: {
    name: string;
    country: string;
  }) => Promise<boolean>;
  onImportCsv?: () => void;
  isSaving?: boolean;
}

// Whether each entity is a business or a person. Maps to the Entity.entity_type
// enum on the backend ('company' | 'person'). Defaults to a business.
const ENTITY_TYPE_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Business', value: 'company' },
  { label: 'Person', value: 'person' },
];

const DEFAULT_ENTITY_TYPE = 'company';

const NAME_MAX = 120;
const COUNTRY_MAX = 60;
const ADDRESS_MAX = 200;

const createEmptyRow = (): QuickAddRow => ({
  id:
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `row-${Math.random().toString(36).slice(2)}-${Date.now()}`,
  name: '',
  entity_type: DEFAULT_ENTITY_TYPE,
  country: '',
  address: '',
  status: 'draft',
  errors: {},
});

const GRID_TEMPLATE =
  '32px minmax(0, 2fr) minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 2fr) 40px 40px';

// entity_type always carries a default, so it does not count toward whether a
// row has been "filled" by the user.
const isRowFilled = (row: QuickAddRow): boolean =>
  Boolean(row.name.trim() || row.country.trim() || row.address.trim());

// Normalized key used to detect duplicate rows within the grid (name + country).
// Mirrors the backend: case- and punctuation-insensitive (lowercase, then strip
// non-alphanumerics).
const normalizeForCompare = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

const dupKey = (name: string, country: string): string =>
  `${normalizeForCompare(name)}|${normalizeForCompare(country)}`;

const validateRow = (
  row: QuickAddRow,
): Partial<Record<keyof QuickAddEntity, string>> => {
  const errors: Partial<Record<keyof QuickAddEntity, string>> = {};
  const name = row.name.trim();
  if (!name) {
    errors.name = 'Name is required';
  } else if (name.length > NAME_MAX) {
    errors.name = `Name must be ${NAME_MAX} characters or fewer`;
  }
  if (row.country.trim().length > COUNTRY_MAX) {
    errors.country = `Country must be ${COUNTRY_MAX} characters or fewer`;
  }
  if (row.address.trim().length > ADDRESS_MAX) {
    errors.address = `Address must be ${ADDRESS_MAX} characters or fewer`;
  }
  return errors;
};

const firstError = (
  errors: Partial<Record<keyof QuickAddEntity, string>>,
): string => {
  const keys = Object.keys(errors) as Array<keyof QuickAddEntity>;
  return keys.length ? errors[keys[0]] || 'Invalid row' : '';
};

export const QuickAddEntities: React.FC<IQuickAddEntitiesProps> = ({
  onSaveAll,
  onCheckDuplicate,
  onImportCsv,
  isSaving = false,
}) => {
  const [isChecking, setIsChecking] = useState(false);
  const [rows, setRows] = useState<QuickAddRow[]>(() => [
    createEmptyRow(),
    createEmptyRow(),
    createEmptyRow(),
  ]);
  const nameRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const focusName = useCallback((rowId: string) => {
    requestAnimationFrame(() => {
      nameRefs.current[rowId]?.focus();
    });
  }, []);

  const updateRow = useCallback(
    (id: string, patch: Partial<QuickAddRow>) => {
      setRows((prev) =>
        prev.map((row) =>
          row.id === id
            ? {
                ...row,
                ...patch,
                // Any manual edit returns an errored/saved row to draft so the
                // user can re-commit it.
                status:
                  patch.status !== undefined ? patch.status : 'draft',
                errors: patch.errors !== undefined ? patch.errors : {},
              }
            : row,
        ),
      );
    },
    [],
  );

  // Pure helper: validate a row and return a copy with the resulting status.
  // Empty rows reset to draft and are treated as skippable.
  const markRow = useCallback((row: QuickAddRow): QuickAddRow => {
    if (!isRowFilled(row)) {
      return { ...row, status: 'draft', errors: {} };
    }
    const errors = validateRow(row);
    return Object.keys(errors).length
      ? { ...row, status: 'error', errors: errors }
      : { ...row, status: 'saved', errors: {} };
  }, []);

  // Validate + commit a single row (used on blur).
  const commitRow = useCallback(
    (id: string) => {
      setRows((prev) =>
        prev.map((row) => (row.id === id ? markRow(row) : row)),
      );
    },
    [markRow],
  );

  const addRow = useCallback(() => {
    const next = createEmptyRow();
    setRows((prev) => [...prev, next]);
    focusName(next.id);
  }, [focusName]);

  const removeRow = useCallback((id: string) => {
    setRows((prev) => {
      if (prev.length <= 1) {
        return [createEmptyRow()];
      }
      return prev.filter((row) => row.id !== id);
    });
  }, []);

  // Enter on a filled name commits the row and starts a fresh one.
  const handleEnterOnRow = useCallback(
    (id: string) => {
      const target = rows.find((row) => row.id === id);
      if (!target) {
        return;
      }
      const marked = markRow(target);
      const isValid = marked.status !== 'error';

      setRows((prev) => {
        const next = prev.map((row) => (row.id === id ? marked : row));
        if (!isValid) {
          return next;
        }
        const empty = next.find(
          (row) => row.id !== id && !isRowFilled(row),
        );
        if (empty) {
          focusName(empty.id);
          return next;
        }
        const fresh = createEmptyRow();
        focusName(fresh.id);
        return [...next, fresh];
      });
    },
    [rows, markRow, focusName],
  );

  const handleSaveAll = useCallback(async () => {
    if (isChecking || isSaving) {
      return;
    }

    const filledRows = rows.filter(isRowFilled);
    if (!filledRows.length) {
      enqueueSnackbar('Add at least one entity before saving.', {
        variant: 'error',
      });
      return;
    }

    // 1. Field validation.
    let markedRows = rows.map(markRow);

    // 2. In-grid duplicate detection (rows that repeat within this list).
    const keyCounts = new Map<string, number>();
    filledRows.forEach((row) => {
      const key = dupKey(row.name, row.country);
      keyCounts.set(key, (keyCounts.get(key) || 0) + 1);
    });
    markedRows = markedRows.map((row) => {
      if (!isRowFilled(row) || row.status === 'error') {
        return row;
      }
      const key = dupKey(row.name, row.country);
      if ((keyCounts.get(key) || 0) > 1) {
        return {
          ...row,
          status: 'error',
          errors: { ...row.errors, name: 'Duplicate row in this list' },
        };
      }
      return row;
    });

    const localErrorCount = markedRows.filter(
      (row) => row.status === 'error',
    ).length;
    if (localErrorCount > 0) {
      setRows(markedRows);
      enqueueSnackbar(
        `${localErrorCount} row${
          localErrorCount > 1 ? 's have' : ' has'
        } errors. Fix them before saving.`,
        { variant: 'error' },
      );
      return;
    }

    // 3. Duplicate detection against entities already in the database.
    if (onCheckDuplicate) {
      setIsChecking(true);
      try {
        const validFilled = markedRows.filter(isRowFilled);
        const results = await Promise.all(
          validFilled.map((row) =>
            onCheckDuplicate({ name: row.name, country: row.country }),
          ),
        );
        const duplicateIds = new Set<string>();
        validFilled.forEach((row, index) => {
          if (results[index]) {
            duplicateIds.add(row.id);
          }
        });

        if (duplicateIds.size > 0) {
          markedRows = markedRows.map((row) =>
            duplicateIds.has(row.id)
              ? {
                  ...row,
                  status: 'error',
                  errors: {
                    ...row.errors,
                    name: 'Already exists in this database',
                  },
                }
              : row,
          );
          setRows(markedRows);
          enqueueSnackbar(
            `${duplicateIds.size} entit${
              duplicateIds.size > 1 ? 'ies' : 'y'
            } already exist in this database. Remove or rename before saving.`,
            { variant: 'error' },
          );
          return;
        }
      } finally {
        setIsChecking(false);
      }
    }

    setRows(markedRows);

    const entities: QuickAddEntity[] = markedRows
      .filter(isRowFilled)
      .map((row) => ({
        name: row.name.trim(),
        entity_type: row.entity_type || DEFAULT_ENTITY_TYPE,
        country: row.country.trim(),
        address: row.address.trim(),
      }));
    onSaveAll(entities);
  }, [rows, markRow, onSaveAll, onCheckDuplicate, isChecking, isSaving]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, rowId: string) => {
      if (event.key !== 'Enter') {
        return;
      }
      event.preventDefault();
      if (event.metaKey || event.ctrlKey) {
        handleSaveAll();
        return;
      }
      handleEnterOnRow(rowId);
    },
    [handleEnterOnRow, handleSaveAll],
  );

  // Pasting multi-line text into a Name input bulk-fills the current row and the
  // rows that follow, creating new rows as needed.
  const handleNamePaste = useCallback(
    (event: React.ClipboardEvent, rowId: string) => {
      const text = event.clipboardData.getData('text');
      if (!text || !/\n/.test(text)) {
        return; // Single line — let the default paste happen.
      }
      event.preventDefault();
      const names = text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      if (!names.length) {
        return;
      }

      setRows((prev) => {
        const startIndex = prev.findIndex((row) => row.id === rowId);
        if (startIndex === -1) {
          return prev;
        }
        const next = [...prev];
        names.forEach((name, offset) => {
          const targetIndex = startIndex + offset;
          if (targetIndex < next.length) {
            next[targetIndex] = {
              ...next[targetIndex],
              name: name,
              status: 'draft',
              errors: {},
            };
          } else {
            next.push({ ...createEmptyRow(), name: name });
          }
        });
        return next;
      });
      enqueueSnackbar(
        `Pasted ${names.length} name${names.length > 1 ? 's' : ''} into rows`,
        { variant: 'success' },
      );
    },
    [],
  );

  const { readyCount, savedCount } = useMemo(() => {
    let ready = 0;
    let saved = 0;
    rows.forEach((row) => {
      if (isRowFilled(row) && Object.keys(validateRow(row)).length === 0) {
        ready += 1;
      }
      if (row.status === 'saved') {
        saved += 1;
      }
    });
    return { readyCount: ready, savedCount: saved };
  }, [rows]);

  const inputBaseStyle: React.CSSProperties = {
    height: '40px',
    width: '100%',
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid var(--bg-border-db)',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontFamily: 'Inter',
    fontSize: '14px',
    outline: 'none',
  };

  const errorInputStyle: React.CSSProperties = {
    border: '1px solid var(--destructive)',
    boxShadow: '0 0 0 1px var(--destructive)',
  };

  const headerCellStyle: React.CSSProperties = {
    color: 'var(--text-secondary)',
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Scrollable region */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          paddingTop: '12px',
          paddingBottom: '12px',
        }}
      >
        <div className='audit-add-modal-cont-body-frame'>
          {/* Helper / keyboard hint row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <p
            style={{
              color: 'var(--text-secondary)',
              fontFamily: 'Inter',
              fontSize: '14px',
              margin: 0,
            }}
          >
            Enter one entity per row. Press{' '}
            <kbd
              style={{
                padding: '2px 6px',
                borderRadius: '4px',
                border: '1px solid var(--bg-border-db)',
                fontSize: '12px',
              }}
            >
              Enter
            </kbd>{' '}
            to save the row and start a new one. Paste a list of names into Name
            to bulk-fill.
          </p>
          <span
            style={{
              color: 'var(--text-secondary)',
              fontFamily: 'Inter',
              fontSize: '13px',
              whiteSpace: 'nowrap',
            }}
          >
            <kbd
              style={{
                padding: '2px 6px',
                borderRadius: '4px',
                border: '1px solid var(--bg-border-db)',
                fontSize: '12px',
              }}
            >
              ⌘ / Ctrl + Enter
            </kbd>{' '}
            Save all
          </span>
        </div>

        {/* Grid card */}
        <div
          style={{
            width: '100%',
            border: '1px solid var(--bg-border-db)',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          {/* Header row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: GRID_TEMPLATE,
              gap: '12px',
              padding: '12px 16px',
              background: 'var(--bg-secondary)',
              borderBottom: '1px solid var(--bg-border-db)',
            }}
          >
            <div style={headerCellStyle}>#</div>
            <div style={headerCellStyle}>
              Name <span className='required-star'>*</span>
            </div>
            <div style={headerCellStyle}>Type</div>
            <div style={headerCellStyle}>Country</div>
            <div style={headerCellStyle}>Address</div>
            <div style={headerCellStyle} aria-hidden />
            <div style={headerCellStyle} aria-hidden />
          </div>

          {/* Body rows */}
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {rows.map((row, index) => {
              const rowBackground =
                row.status === 'saved'
                  ? 'color-mix(in srgb, var(--success) 6%, transparent)'
                  : row.status === 'error'
                  ? 'color-mix(in srgb, var(--destructive) 6%, transparent)'
                  : 'transparent';
              return (
                <div
                  key={row.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: GRID_TEMPLATE,
                    gap: '12px',
                    padding: '10px 16px',
                    alignItems: 'center',
                    background: rowBackground,
                    borderBottom: '1px solid var(--bg-border-db)',
                  }}
                >
                  <div
                    style={{
                      color: 'var(--text-secondary)',
                      fontFamily: 'Inter',
                      fontSize: '13px',
                    }}
                  >
                    {index + 1}
                  </div>
                  <input
                    ref={(el) => {
                      nameRefs.current[row.id] = el;
                    }}
                    type='text'
                    value={row.name}
                    placeholder='Entity name'
                    aria-invalid={Boolean(row.errors.name)}
                    style={{
                      ...inputBaseStyle,
                      ...(row.errors.name ? errorInputStyle : {}),
                    }}
                    onChange={(e) => updateRow(row.id, { name: e.target.value })}
                    onKeyDown={(e) => handleKeyDown(e, row.id)}
                    onPaste={(e) => handleNamePaste(e, row.id)}
                    onBlur={() => {
                      if (row.name.trim()) {
                        commitRow(row.id);
                      }
                    }}
                  />
                  <select
                    value={row.entity_type}
                    style={inputBaseStyle}
                    onChange={(e) =>
                      updateRow(row.id, { entity_type: e.target.value })
                    }
                    onKeyDown={(e) => handleKeyDown(e, row.id)}
                  >
                    {ENTITY_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type='text'
                    value={row.country}
                    placeholder='US'
                    aria-invalid={Boolean(row.errors.country)}
                    style={{
                      ...inputBaseStyle,
                      ...(row.errors.country ? errorInputStyle : {}),
                    }}
                    onChange={(e) =>
                      updateRow(row.id, { country: e.target.value })
                    }
                    onKeyDown={(e) => handleKeyDown(e, row.id)}
                  />
                  <input
                    type='text'
                    value={row.address}
                    placeholder='123 Main St'
                    aria-invalid={Boolean(row.errors.address)}
                    style={{
                      ...inputBaseStyle,
                      ...(row.errors.address ? errorInputStyle : {}),
                    }}
                    onChange={(e) =>
                      updateRow(row.id, { address: e.target.value })
                    }
                    onKeyDown={(e) => handleKeyDown(e, row.id)}
                    onBlur={() => {
                      if (row.name.trim()) {
                        commitRow(row.id);
                      }
                    }}
                  />
                  {/* Status indicator */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {row.status === 'saved' && (
                      <CheckCircleOutlineOutlinedIcon
                        style={{ color: 'var(--success)', fontSize: '20px' }}
                        aria-label='Row saved'
                      />
                    )}
                    {row.status === 'error' && (
                      <Tooltip title={firstError(row.errors)} arrow>
                        <ErrorOutlineOutlinedIcon
                          style={{
                            color: 'var(--destructive)',
                            fontSize: '20px',
                          }}
                          aria-label={firstError(row.errors)}
                        />
                      </Tooltip>
                    )}
                  </div>
                  {/* Delete */}
                  <button
                    type='button'
                    aria-label={`Remove row ${index + 1}`}
                    onClick={() => removeRow(row.id)}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--text-secondary)',
                      padding: 0,
                    }}
                  >
                    <DeleteOutlineOutlinedIcon style={{ fontSize: '20px' }} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Footer of the grid card */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
            }}
          >
            <button
              type='button'
              onClick={addRow}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-primary)',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 500,
                padding: 0,
              }}
            >
              <AddOutlinedIcon style={{ fontSize: '18px' }} />
              Add another row
            </button>
            <button
              type='button'
              onClick={onImportCsv}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-secondary)',
                fontFamily: 'Inter',
                fontSize: '14px',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <FileUploadOutlinedIcon style={{ fontSize: '18px' }} />
              Or import CSV
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* Pinned footer: counters + actions */}
      <div
        className='audit-modal-cont-footer'
        style={{
          flexShrink: 0,
          paddingTop: '12px',
          borderTop: '1px solid var(--bg-border-db)',
        }}
      >
          <div
            className='audit-modal-cont-footer-content'
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span
              style={{
                color: 'var(--text-secondary)',
                fontFamily: 'Inter',
                fontSize: '14px',
              }}
            >
              <strong style={{ color: 'var(--text-primary)' }}>
                {readyCount}
              </strong>{' '}
              ready · {savedCount} validated
            </span>
            <div
              className='audit-modal-cont-footer-content-btn-upload'
              style={{
                flex: '0 0 auto',
                minWidth: '120px',
                ...(readyCount === 0 || isSaving || isChecking
                  ? { opacity: 0.5, cursor: 'not-allowed' }
                  : {}),
              }}
              onClick={
                readyCount === 0 || isSaving || isChecking
                  ? undefined
                  : handleSaveAll
              }
            >
              <div className='audit-modal-cont-footer-content-btn-text'>
                {isSaving || isChecking ? (
                  <span className='flex items-center justify-center'>
                    <CircularProgress
                      style={{
                        color: '#FFF',
                        width: '20px',
                        height: '20px',
                        marginRight: '8px',
                      }}
                    />
                    {isChecking ? 'Checking...' : 'Saving...'}
                  </span>
                ) : (
                  `Save (${readyCount})`
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default QuickAddEntities;
